import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Gift, HelpCircle, LogOut, ShieldCheck, MessageCircle, Mail, Smartphone, UserPlus, LogIn } from "lucide-react";
import { useAuth } from "../lib/auth";
import InstallGuideModal, { canShowInstallGuide } from "../components/InstallGuideModal";

const SUPPORT_PHONE_RAW = "447350695847";
const SUPPORT_PHONE_DISPLAY = "+44 7350 695847";
const SUPPORT_EMAIL = "help_mrscashback@outlook.com";

// Minimal Snapchat ghost icon — lucide doesn't ship a brand mark, so we
// inline a clean SVG silhouette that matches the rest of our iconography.
function SnapchatGhost({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2.2c2.7 0 5 2 5.4 4.7.1.7.1 1.7 0 2.7 0 .3 0 .6.2.7l.7.3c.4.2.7.4.7.8 0 .5-.6.8-1.4 1.1-.3.1-.6.2-.8.4-.1.1-.1.3-.1.5.3.7.8 1.3 1.4 1.8.6.4 1.3.7 2.1.8.3 0 .5.3.4.6-.2.8-1.9 1-2.6 1.1-.1 0-.1.1-.2.3-.1.3-.2.5-.3.7-.1.2-.3.3-.5.3-.3 0-.6-.1-.9-.2-.4-.1-.8-.2-1.4-.2-.4 0-.7.1-1.1.2-.5.2-1 .5-1.6.8-.6.4-1.2.5-1.8.5h-.1c-.6 0-1.1-.2-1.7-.5-.7-.4-1.1-.6-1.6-.8-.4-.1-.7-.2-1.1-.2-.5 0-1 .1-1.4.2-.3.1-.6.2-.9.2-.3 0-.4-.2-.5-.3-.1-.2-.2-.5-.3-.7 0-.1-.1-.2-.2-.3-.7-.1-2.4-.3-2.6-1.1-.1-.3.1-.5.4-.6.7-.1 1.5-.4 2.1-.8.6-.5 1.1-1.1 1.4-1.8 0-.2.1-.3-.1-.5-.2-.2-.5-.3-.8-.4-.8-.3-1.4-.6-1.4-1.1 0-.4.3-.6.7-.8l.7-.3c.2-.1.2-.4.2-.7-.1-1-.1-1.9 0-2.7C7 4.2 9.3 2.2 12 2.2z"
      />
    </svg>
  );
}

export default function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [installEligible, setInstallEligible] = useState(false);

  // Recompute on mount + on focus, since the user could install while the page is open.
  useEffect(() => {
    const recompute = () => setInstallEligible(canShowInstallGuide());
    recompute();
    window.addEventListener("focus", recompute);
    return () => window.removeEventListener("focus", recompute);
  }, []);

  // Auto-cancel the confirm state after 4 seconds
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 4000);
    return () => clearTimeout(t);
  }, [confirming]);

  if (!user) return <ProfileGuest />;

  const onLogout = async () => {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="page fade-in">
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1, margin: 0, marginBottom: 22 }}>
        Profile
      </h1>

      <div className="card" style={{
        textAlign: "center", padding: 24,
        background: "#FFFFFF", border: "1px solid var(--border)",
        borderRadius: 28,
        boxShadow: "0 14px 28px -22px rgba(11,36,71,0.15)",
      }}>
        <div style={{
          width: 80, height: 80, borderRadius: 40, margin: "0 auto",
          background: "var(--primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 14px 28px -14px rgba(226, 92, 18, 0.55)",
        }}>
          <span style={{ color: "#FFFFFF", fontSize: 32, fontWeight: 800 }}>
            {(user.name || user.email)[0]?.toUpperCase()}
          </span>
        </div>
        <div data-testid="profile-name" style={{ fontSize: 22, fontWeight: 800, marginTop: 14, color: "var(--navy)" }}>{user.name}</div>
        <div data-testid="profile-email" style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 4 }}>{user.email}</div>

        {user.role === "admin" && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "var(--primary)",
            borderRadius: 999,
            padding: "4px 12px", marginTop: 12, color: "#FFFFFF",
          }}>
            <ShieldCheck size={14} />
            <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.4 }}>Admin</span>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
        <div className="card" style={{
          flex: 1, padding: 18,
          background: "#FFFFFF", border: "1px solid var(--border)",
          borderRadius: 22,
        }}>
          <div className="label" style={{ color: "var(--text-secondary)" }}>Balance</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: "var(--primary-dark)" }}>£{user.balance.toFixed(2)}</div>
        </div>
        <div className="card" style={{
          flex: 1, padding: 18,
          background: "#FFFFFF", border: "1px solid var(--border)",
          borderRadius: 22,
        }}>
          <div className="label" style={{ color: "var(--text-secondary)" }}>Points</div>
          <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, color: "var(--accent-pink)" }}>{user.points || 0} pt{(user.points || 0) === 1 ? "" : "s"}</div>
        </div>
      </div>

      <button
        data-testid="refer-a-friend-link"
        onClick={() => navigate("/refer")}
        style={{
          display: "flex", alignItems: "center", gap: 14,
          background: "var(--accent-pink)",
          border: "1px solid var(--accent-pink)",
          borderRadius: 28, padding: 16, marginTop: 16, width: "100%", textAlign: "left",
          boxShadow: "0 16px 32px -18px rgba(230, 59, 90, 0.55)",
        }}
      >
        <span style={{
          width: 44, height: 44, borderRadius: 12,
          background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Gift size={22} color="#FFFFFF" />
        </span>
        <span style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#FFFFFF" }}>Refer a Friend · Earn up to £5</div>
          <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, marginTop: 2 }}>
            You both get a bonus when their first receipt is approved
          </div>
        </span>
        <ChevronRight size={20} color="rgba(255,255,255,0.85)" />
      </button>

      <button
        data-testid="how-it-works-link"
        onClick={() => navigate("/how-it-works")}
        style={{
          display: "flex", alignItems: "center", gap: 14,
          background: "var(--burgundy)", border: "1px solid var(--burgundy)",
          borderRadius: 28, padding: 16, marginTop: 16, width: "100%", textAlign: "left",
          boxShadow: "0 16px 32px -18px rgba(184, 64, 80, 0.5)",
        }}
      >
        <span style={{
          width: 44, height: 44, borderRadius: 12,
          background: "rgba(255,255,255,0.22)", border: "1px solid rgba(255,255,255,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <HelpCircle size={22} color="#FFFFFF" />
        </span>
        <span style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#FFFFFF" }}>How It Works</div>
          <div style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, marginTop: 2 }}>Quick guide to earning cash back</div>
        </span>
        <ChevronRight size={20} color="rgba(255,255,255,0.85)" />
      </button>

      {installEligible && (
        <button
          data-testid="profile-install-app-button"
          onClick={() => setShowInstall(true)}
          style={{
            display: "flex", alignItems: "center", gap: 14,
            background: "#FFFFFF",
            border: "1px solid var(--border)",
            borderRadius: 28, padding: 16, marginTop: 16, width: "100%", textAlign: "left",
          }}
        >
          <span style={{
            width: 44, height: 44, borderRadius: 12,
            background: "var(--burgundy)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Smartphone size={22} color="#FFFFFF" />
          </span>
          <span style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Install Cashback App</div>
            <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 2 }}>
              Add to home screen for one-tap access
            </div>
          </span>
          <ChevronRight size={20} color="var(--text-muted)" />
        </button>
      )}

      <div
        data-testid="profile-support-card"
        style={{
          background: "#FFFFFF",
          border: "1px solid var(--border)",
          borderRadius: 28,
          padding: 16,
          marginTop: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            width: 36, height: 36, borderRadius: 10,
            background: "var(--peach-tint-soft)", border: "1px solid var(--peach-border)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <HelpCircle size={18} color="var(--primary)" />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Need support?</div>
            <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 2 }}>
              We usually reply within a few hours.
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 14 }}>
          <a
            data-testid="profile-support-whatsapp"
            href={`https://wa.me/${SUPPORT_PHONE_RAW}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "#25D366", color: "#fff",
              borderRadius: 999, padding: "12px 16px",
              fontWeight: 800, fontSize: 14,
              textDecoration: "none",
              minHeight: 44,
            }}
          >
            <MessageCircle size={18} />
            <span style={{ flex: 1 }}>WhatsApp</span>
            <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.95 }}>{SUPPORT_PHONE_DISPLAY}</span>
          </a>

          <a
            data-testid="profile-support-email"
            href={`mailto:${SUPPORT_EMAIL}?subject=Mrs%20CashBack%20Support`}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "#0F8C4A", color: "#fff",
              borderRadius: 999, padding: "12px 16px",
              fontWeight: 800, fontSize: 13,
              textDecoration: "none",
              minHeight: 44,
              wordBreak: "break-all",
            }}
          >
            <Mail size={18} />
            <span style={{ flex: 1 }}>Email</span>
            <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.95 }}>{SUPPORT_EMAIL}</span>
          </a>

          <a
            data-testid="profile-support-snapchat"
            href="https://www.snapchat.com/add/misscashback"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "flex", alignItems: "center", gap: 10,
              background: "#FFFC00", color: "var(--navy)",
              borderRadius: 999, padding: "12px 16px",
              fontWeight: 800, fontSize: 14,
              textDecoration: "none",
              minHeight: 44,
            }}
          >
            <SnapchatGhost size={18} />
            <span style={{ flex: 1 }}>Snapchat</span>
            <span style={{ fontSize: 12, fontWeight: 700, opacity: 0.85 }}>misscashback</span>
          </a>
        </div>
      </div>

      <button
        data-testid="logout-button"
        onClick={onLogout}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          background: confirming ? "var(--error)" : "var(--surface)",
          border: `1px solid ${confirming ? "var(--error)" : "rgba(217, 72, 72, 0.4)"}`,
          borderRadius: 999, padding: "16px 0", marginTop: 24, width: "100%",
          color: confirming ? "#FFFFFF" : "var(--error)",
          fontWeight: 800, fontSize: 15,
          transition: "background .18s ease, color .18s ease, border-color .18s ease",
        }}
      >
        <LogOut size={20} />
        {confirming ? "Tap again to confirm" : "Sign out"}
      </button>

      <InstallGuideModal open={showInstall} onClose={() => setShowInstall(false)} />
    </div>
  );
}

/* ---------- Guest view (no account / signed out) ---------- */
function ProfileGuest() {
  const { openAuthGate } = useAuth();
  return (
    <div className="page fade-in" data-testid="profile-guest">
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1, margin: 0, marginBottom: 22 }}>
        Profile
      </h1>

      <div className="card" style={{
        textAlign: "center", padding: "30px 24px 26px",
        background: "#FFFFFF", border: "1px solid var(--border)",
        borderRadius: 28,
        boxShadow: "0 14px 28px -22px rgba(11,36,71,0.15)",
      }}>
        <div style={{
          width: 84, height: 84, borderRadius: 42, margin: "0 auto 14px",
          background: "var(--primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 14px 28px -14px rgba(226, 92, 18, 0.55)",
        }}>
          <UserPlus size={36} color="#FFFFFF" />
        </div>

        <h2 style={{
          color: "var(--navy)", fontSize: 22, fontWeight: 800,
          letterSpacing: -0.4, margin: "0 0 6px",
        }}>
          Sign up or log in
        </h2>
        <p style={{
          color: "var(--text-secondary)", fontSize: 14, lineHeight: 1.5,
          margin: "0 0 22px", padding: "0 6px",
        }}>
          Create an account to upload receipts and cash out your rewards.
        </p>

        <button
          data-testid="profile-guest-register"
          onClick={() => openAuthGate()}
          className="btn-primary"
          style={{
            width: "100%", display: "inline-flex", alignItems: "center",
            justifyContent: "center", gap: 8,
            padding: "14px 16px",
            fontSize: 13, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase",
          }}
        >
          <UserPlus size={16} />
          Create account
          <ChevronRight size={16} />
        </button>

        <button
          data-testid="profile-guest-login"
          onClick={() => openAuthGate()}
          style={{
            width: "100%", marginTop: 10,
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            background: "transparent",
            border: "1px solid var(--border-strong)",
            borderRadius: 999, padding: "13px 16px",
            color: "var(--text-primary)",
            fontSize: 13, fontWeight: 800, letterSpacing: 0.4,
            cursor: "pointer",
          }}
        >
          <LogIn size={16} />
          I already have an account
        </button>
      </div>
    </div>
  );
}
